import React, { useEffect, useState } from "react"
import {
  message,
  Modal,
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  DatePicker,
  Row,
  Col,
  Descriptions,
  Divider,
  Typography,
} from "antd"
import moment from "moment"
import WarehouseService from "src/services/WarehouseService"
import SelectPackage from "src/components/Dropdown/SelectPackage"
import SelectLocalRoaming from "src/components/Dropdown/SelectLocalRoaming"
import SelectSimType from "src/components/Dropdown/SelectSimType"
import SelectTypeSim from "src/components/Dropdown/SelectTypeSim"
import { formatCommas, parseFormat } from "src/utils"
import { handleUpdatePriceBeforeVAT } from "src/utils/invoice"

const { Option } = Select
const { Text } = Typography

const statusMap = {
  0: "Chưa kích hoạt",
  1: "Đã kích hoạt",
  2: "Đã vô hiệu",
  3: "Đã gia hạn",
  4: "Đã cấp lại",
  5: "Đã thu hồi",
}

const ViewDetailModal = ({
  open,
  onClose,
  id,
  onlyView,
  providers = [],
  carriers = [],
  countries = [],
  initialData = null,
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [subscriberInfo, setSubscriberInfo] = useState(null)

  useEffect(() => {
    if (initialData) {
      const data = transformData(initialData)
      form.setFieldsValue(data)
      updateSubscriberInfo(data)
    } else if (id) {
      fetchData(id)
    }
  }, [id, initialData])

  const transformData = data => ({
    ...data,
    status: statusMap[data.status],
    import_date: data.created_on ? moment(data.created_on) : null,
    activation_deadline: data.activation_deadline
      ? moment(data.activation_deadline)
      : null,
    start_date: data?.sim_user?.start_date
      ? moment(data?.sim_user?.start_date)
      : null,
    end_date: data?.sim_user?.end_date
      ? moment(data?.sim_user?.end_date)
      : null,
    providers_id: data.supplier?.name,
    package_id: data?.package?.name,
    country_id: data.country?.title,
    carrier_id: data.carrier?.name,
    quantity: data.quantity || "",
    approval_batch_number: data.approval?.batch_number,
    total_after_vat: data.price_after_vat ? data.price_after_vat : "",
    price_buy: data.price_before_vat ? data.price_before_vat : "",
    fee_vat: data.fee_vat ? data.fee_vat : 10,
    price: data.package_unit_price ? data.package_unit_price : "",
    fee_connection: data.connection_fee ? data.connection_fee : "",
    fee_sim: data.sim_fee ? data.sim_fee : "",
    unit_price: data.unit_price ? data.unit_price : "",
  })

  const fetchData = async id => {
    setLoading(true)
    try {
      const res = await WarehouseService.warehouseDetail(id)
      const data = res?.success
      if (data) {
        const transformed = transformData(data)
        form.setFieldsValue(transformed)
        updateSubscriberInfo(transformed)
      }
    } catch (err) {
      console.error("Fetch failed", err)
      message.error("Không thể lấy thông tin phiếu xuất kho")
    } finally {
      setLoading(false)
    }
  }
  const updateSubscriberInfo = data => {
    if (data.sim_type === "Đang hoạt động" && data.subscriber_info) {
      setSubscriberInfo(data.subscriber_info)
    } else {
      setSubscriberInfo(null)
    }
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      const payload = {
        ...values,
        import_date: values.import_date?.format("YYYY-MM-DD"),
        activation_deadline: values.activation_deadline?.format("YYYY-MM-DD"),
      }

      setLoading(true)
      await WarehouseService.warehouseUpdate(id, payload)
      message.success("Cập nhật thành công")
      onClose()
    } catch (err) {
      console.error("Save failed", err)
      message.error("Cập nhật thất bại")
    } finally {
      setLoading(false)
    }
  }
  const handleUpdatePrice = async () => {
    try {
      const values = await form.validateFields(["sale_price"])
      const price = values.sale_price

      setLoading(true)
      await WarehouseService.updatePriceSim(id, { price }) // truyền đúng id
      message.success("Cập nhật giá SIM thành công!")
    } catch (err) {
      console.error(err)
      if (err?.errorFields) return
      message.error("Cập nhật giá SIM thất bại!")
    } finally {
      setLoading(false)
    }
  }

  const renderSelect = (name, label, options) => (
    <Form.Item label={label} name={name}>
      <Select
        placeholder={`Chọn ${label.toLowerCase()}`}
        allowClear
        disabled={onlyView}
      >
        {options.map(({ id, name, title }) => (
          <Option key={id} value={id}>
            {name || title}
          </Option>
        ))}
      </Select>
    </Form.Item>
  )

  return (
    <Modal
      open={open}
      title={onlyView ? "Chi tiết SIM" : "Sửa thông tin SIM"}
      onCancel={onClose}
      width={800}
      footer={
        onlyView
          ? null
          : [
              <Button key="cancel" onClick={onClose}>
                Hủy
              </Button>,
              <Button key="save" type="primary" onClick={handleSave}>
                Lưu
              </Button>,
            ]
      }
    >
      {loading ? (
        <p>Đang tải dữ liệu...</p>
      ) : (
        <>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Số lô nhập" name="approval_batch_number">
                  <Input placeholder="Nhập số lô nhập" disabled={onlyView} />
                </Form.Item>
              </Col>{" "}
              {/* <Col span={12}>
                {renderSelect("country_id", "Quốc gia", countries)}
              </Col> */}
              <Col span={12}>
                {renderSelect("providers_id", "Nhà cung cấp", providers)}
              </Col>
              <Col span={12}>
                {renderSelect("carrier_id", "Nhà mạng", carriers)}
              </Col>
              <Col span={12}>
                <Form.Item label="Loại SIM" name="type_sim">
                  <SelectTypeSim disabled={onlyView} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Loại hình SIM" name="sim_type">
                  <SelectSimType disabled={onlyView} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Local/Roaming" name="scope">
                  <SelectLocalRoaming disabled={onlyView} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Loại thanh toán" name="payment_type">
                  <Select
                    disabled={onlyView}
                    options={[
                      { label: "Trả trước", value: "1" },
                      { label: "Trả sau", value: "2" },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Số seri" name="serial_number">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Số thuê bao" name="phone_number">
                  <Input disabled={onlyView} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Đơn giá SIM" name="unit_price">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    placeholder="Nhập đơn giá"
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Loại tiền tệ" name="currency_type">
                  <Select
                    disabled={onlyView}
                    options={[
                      { label: "VND", value: "vnd" },
                      { label: "USD", value: "usd" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Ngày nhập" name="import_date">
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Thời gian phải kích hoạt"
                  name="activation_deadline"
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col>
              {/* <Col span={12}>
                <Form.Item label="Ngày kích hoạt" name="start_date">
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col>
               <Col span={12}>
                <Form.Item label="Ngày hết hạn" name="end_date">
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col> */}
              <Col span={12}>
                <Form.Item
                  name="fee_connection"
                  label="Phí hòa mạng (VND)"
                  rules={[
                    { required: true, message: "Vui lòng nhập phí hòa mạng" },
                  ]}
                >
                  <InputNumber
                    min={0}
                    placeholder="Nhập phí hòa mạng"
                    formatter={value => formatCommas(value)}
                    parser={value => parseFormat(value)}
                    style={{ width: "100%" }}
                    onChange={() => handleUpdatePriceBeforeVAT(form)}
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="fee_sim"
                  label="Phí sim (VND)"
                  rules={[{ required: true, message: "Vui lòng nhập phí sim" }]}
                >
                  <InputNumber
                    min={0}
                    placeholder="Nhập phí sim"
                    formatter={value => formatCommas(value)}
                    parser={value => parseFormat(value)}
                    style={{ width: "100%" }}
                    onChange={() => handleUpdatePriceBeforeVAT(form)}
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="price"
                  label="Giá gói cước (VND)"
                  rules={[
                    { required: true, message: "Vui lòng nhập giá gói cước" },
                  ]}
                >
                  <InputNumber
                    min={0}
                    placeholder="Nhập giá gói cước"
                    formatter={value => formatCommas(value)}
                    parser={value => parseFormat(value)}
                    style={{ width: "100%" }}
                    onChange={() => handleUpdatePriceBeforeVAT(form)}
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="price_buy"
                  label="Giá trước VAT (VND)"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng nhập giá trước VAT",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    disabled={onlyView}
                    placeholder="Nhập giá trước VAT"
                    formatter={value => formatCommas(value)}
                    parser={value => parseFormat(value)}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="fee_vat"
                  label="VAT (%)"
                  rules={[{ required: true, message: "Vui lòng nhập VAT" }]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    placeholder="Nhập VAT"
                    style={{ width: "100%" }}
                    onChange={() => handleUpdatePriceBeforeVAT(form)}
                    disabled={onlyView}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="total_after_vat"
                  label="Giá sau VAT (VND)"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng nhập giá sau VAT",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    placeholder="Nhập sau VAT"
                    formatter={value => formatCommas(value)}
                    parser={value => parseFormat(value)}
                    style={{ width: "100%" }}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Gói cước" name="package_id">
                  <Input disabled={onlyView} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Trạng thái" name="status">
                  <Input disabled={onlyView} />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {subscriberInfo && (
            <>
              <Divider orientation="left">Thông tin thuê bao</Divider>
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Chủ thuê bao">
                  <Text strong>{subscriberInfo.customer_name || ""}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Gói cước">
                  <Text strong>{subscriberInfo.package_name || ""}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Ngày đăng ký">
                  <Text strong>
                    {subscriberInfo.registration_date
                      ? moment(subscriberInfo.registration_date).format(
                          "DD/MM/YYYY",
                        )
                      : ""}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </>
          )}
        </>
      )}
    </Modal>
  )
}

export default ViewDetailModal
